import { BUSINESSES, type EditionBusinessKey } from "./businesses";
import {
  getServiceDetailPath,
  getServiceDetailSlug,
  getServiceHub,
  SERVICE_HUBS,
  type ServiceHub,
  type ServiceHubFaq,
} from "./serviceHubs";
import type { BusinessService } from "@/lib/api/types";

type ServiceCopy = {
  intro: string;
  expect: readonly string[];
  faq: readonly ServiceHubFaq[];
  photoCategory?: string;
  photosTitle?: string;
};

const references: ServiceHubFaq = {
  question: "Should I bring reference photos?",
  answer: "Yes. Bring a few examples of the shape or finish you like, and tell your stylist how you usually wear your hair.",
};

const womensCut: ServiceCopy = {
  intro: "A haircut shaped around the length, movement, and finish you want—from a small refresh to a new silhouette. Talk through your preferred length, layers, and everyday styling routine with your stylist.",
  expect: [
    "Discuss how much length you want to keep and where you would like more shape or movement.",
    "Bring reference photos and mention how much time you usually spend styling your hair.",
    "If you are planning a major change or have particularly long or thick hair, mention it when booking so your stylist can plan the time needed.",
  ],
  faq: [references, {
    question: "Can I keep my length and still change the shape?",
    answer: "Yes. Tell your stylist that keeping length is your priority. Discuss layers, face framing, or a light trim before the cut begins.",
  }],
  photoCategory: "haircuts",
  photosTitle: "Latest haircut photos",
};

const childCut: ServiceCopy = {
  intro: "A haircut for younger clients, with time to talk through the desired length and shape. Share your child's preferences and anything that helps them feel comfortable during a haircut.",
  expect: [
    "Discuss the length and style with your child and stylist before the cut starts.",
    "Let your stylist know if this is a first haircut or if your child is sensitive to clippers or blow-dryers.",
    "A reference photo can help everyone agree on the finished look.",
  ],
  faq: [{
    question: "What should I mention before the appointment?",
    answer: "Share your child's age, current hair length, and any concerns about sitting for a haircut. Your stylist can help you choose the appropriate service.",
  }, {
    question: "Can we choose an easy-to-manage style?",
    answer: "Tell your stylist about your child's usual routine and how much styling you want to do at home. Use that as the starting point for choosing the shape.",
  }],
};

const perm: ServiceCopy = {
  intro: "Add waves, curls, or volume with a perm planned around your current hair and the texture you want. Share examples of the curl pattern you like and your previous color or chemical-service history.",
  expect: [
    "Share photos of your current hair and the waves or curls you would like.",
    "Discuss your hair's length, condition, and previous color or chemical treatments before deciding on the approach.",
    "Ask your stylist when to wash your hair and how to style the new texture at home.",
  ],
  faq: [{
    question: "Can I choose between waves and more defined curls?",
    answer: "Bring examples of the texture you like. Your stylist will explain what is achievable with your current length and hair condition.",
  }, {
    question: "What if my hair has been colored or bleached?",
    answer: "Include your hair history in your service request or discuss it with your stylist before booking. They will review whether the service is suitable for your hair.",
  }],
  photoCategory: "perms",
  photosTitle: "Latest perm photos",
};

const COPY: Record<string, ServiceCopy> = {
  "mens-haircut": {
    intro: "A haircut shaped around your preferred length, texture, and everyday routine. Bring examples of the finish you like and discuss the shape with your stylist before the cut begins.",
    expect: [
      "Talk through your preferred length, shape, and how you usually style your hair.",
      "Mention whether you want a small refresh or a more substantial change.",
      "For hair below the chin, review Edition Studio's long-length option when booking; longer cuts may need additional time and cost more.",
    ],
    faq: [references, { question: "Which option should I book for longer hair?", answer: "Edition Studio offers a Long Length (Below Chin) option for men's haircuts. Check that option and its price when booking if your hair is below the chin." }],
    photoCategory: "haircuts",
    photosTitle: "Latest haircut photos",
  },
  "womens-haircut": womensCut,
  "kids-haircut": childCut,
  "boys-haircut": childCut,
  "girls-haircut": childCut,
  "bang-cut": {
    intro: "Refresh your fringe or explore a new shape around your face. A bang cut focuses on the front section of your hair, with the length and shape discussed before cutting.",
    expect: [
      "Tell your stylist whether you are maintaining existing bangs or considering a new fringe.",
      "Discuss where you want the bangs to sit and how you normally part and style your hair.",
      "If you also want layers or length adjusted elsewhere, choose a full haircut.",
    ],
    faq: [{
      question: "Is this a full haircut?",
      answer: "This service focuses on bangs. Book a full haircut if you want the rest of your hair reshaped or trimmed too.",
    }, references],
  },
  "mens-perm": perm,
  "womens-perm": perm,
  "volume-straight-perm": {
    intro: "A smoothing service tailored to how much wave and volume you want to reduce. Discuss the balance between a straighter finish and the movement you want to keep.",
    expect: [
      "Talk through the areas you want smoother and the volume you want to retain.",
      "Share any previous coloring, bleaching, or straightening so your stylist can assess your hair before the service.",
      "Ask for washing and styling guidance specific to the treatment used.",
    ],
    faq: [{
      question: "How is this different from a curly perm?",
      answer: "A curly perm adds waves or curls. Volume Straight Perm focuses on reducing wave and smoothing the hair; discuss how straight you want the finish with your stylist.",
    }, {
      question: "Is a haircut included?",
      answer: "Eddie's Edition's Volume Straight Perm includes a haircut, as listed in the service description.",
    }],
  },
  "hair-straightening": {
    intro: "A Japanese-method straightening service for a smoother, straighter finish. Start with a service request so your stylist can review your hair's condition and treatment history.",
    expect: [
      "Share current-hair photos and explain the finish you want in your service request.",
      "Include your color, bleach, perm, and previous straightening history.",
      "The listed service includes a wash, treatment, and flat-iron finish. Your stylist will explain the aftercare for your hair.",
    ],
    faq: [{
      question: "What if my hair is heavily bleached or damaged?",
      answer: "Edition Studio lists this service as not recommended for heavily bleached or damaged hair. Share clear photos and your hair history so your stylist can advise before you book.",
    }, {
      question: "Does this add curls?",
      answer: "No. This service is intended for a straighter finish. Choose a perm service if your goal is waves or curls.",
    }],
  },
  "blowout-wash-and-style": {
    intro: "A wash and professional blow-dry for a polished finish, whether you want smoothness, movement, or volume. Bring a reference for the look you have in mind.",
    expect: [
      "Discuss your preferred finish and how you want your hair parted.",
      "The service includes a shampoo and professional blow-dry styling.",
      "Mention any event timing or special styling requests when booking.",
    ],
    faq: [{
      question: "Is washing included?",
      answer: "Yes. Edition Studio's Blowout (Wash & Style) includes a shampoo and blow-dry styling.",
    }, {
      question: "Does this include a haircut?",
      answer: "This is a styling service. Select a haircut as well if you would like your length or shape changed.",
    }],
  },
  "beard-trim": {
    intro: "Tidy your beard's length and shape while keeping the look you want. Discuss your preferred outline, fullness, and areas you are growing out before the trim begins.",
    expect: [
      "Agree on the length to keep and the overall beard shape.",
      "Point out areas you are growing out or would like your stylist to leave fuller.",
      "If you also want a haircut, select it as an additional service when booking.",
    ],
    faq: [{
      question: "Can I keep the length and just tidy the shape?",
      answer: "Tell your stylist you want to preserve length and identify the areas you want cleaned up before the trim starts.",
    }, {
      question: "Is a haircut included?",
      answer: "A beard trim is listed separately from a haircut. Select both if you would like both services.",
    }],
  },
  "full-shave": {
    intro: "A clean-shaven finish. Discuss your usual shaving routine and any areas that need extra care with your stylist before the service.",
    expect: ["Explain your preferred finish and mention any shaving sensitivities.", "Confirm the details of the service with your stylist before beginning."],
    faq: [{ question: "Should I choose a shave or a beard trim?", answer: "Choose a beard trim to keep and shape facial hair. A full shave is for a clean-shaven finish." }],
  },
};

const requestFaq: ServiceHubFaq = {
  question: "How does a service request work?",
  answer: "Choose a stylist and share photos of your current hair and desired result. Your stylist will review the request and send the price, appointment length, and booking link.",
};

/** Editorial details supplement the live catalog; business-specific offers stay separate. */
export const buildCatalogServiceHub = (
  service: BusinessService,
  business: EditionBusinessKey,
): ServiceHub => {
  const slug = getServiceDetailSlug(service.title);
  const brand = BUSINESSES[business].branding.displayName;
  const curated = business === "eddies" ? getServiceHub(service.title) : undefined;
  const isColor = slug === "color";
  const requiresRequest = isColor || service.hasConsultation === true;
  const copy = COPY[slug];
  const description = service.description?.trim();
  const fallback: ServiceCopy = {
    intro: `${service.title} at ${brand}. Share the result you have in mind with your stylist and review the service details before booking.`,
    expect: [
      "Tell your stylist about the result you want and any relevant hair history.",
      "Review the service details, timing, and price with your selected stylist.",
    ],
    faq: [{ question: "How do I choose a stylist?", answer: "Open a stylist's profile to see their work and available services, then continue to booking or send a service request." }],
  };
  const content = copy ?? fallback;
  const intro = isColor ? SERVICE_HUBS.color.intro : curated?.intro ??
    (copy && (!description || description.length < 100) ? content.intro : description ?? content.intro);
  const fromPrice = requiresRequest || service.price <= 0 ? undefined : service.price;
  const priceLine = requiresRequest
    ? SERVICE_HUBS.color.priceLine
    : curated?.fromPrice !== undefined
      ? curated.priceLine.replace(`$${curated.fromPrice}`, `$${service.price}`)
      : fromPrice !== undefined
        ? `From $${fromPrice}+ · Price varies by stylist`
        : "Price to be determined";
  let faq = isColor ? SERVICE_HUBS.color.faq : curated?.faq ?? content.faq;
  // The live catalog is the source for price, including in the existing haircut FAQ.
  if (curated?.fromPrice !== undefined) {
    faq = faq.map((item) => ({ ...item, answer: item.answer.replace(`$${curated.fromPrice}`, `$${service.price}`) }));
  }
  if (requiresRequest && !faq.some((item) => item.question === requestFaq.question)) {
    faq = [requestFaq, ...faq];
  }
  if (business === "eddies" && slug === "kids-haircut") {
    faq = [{
      question: "What ages is this service for?",
      answer: "Eddie's Edition lists the Kid's Haircut for ages 3–12. For ages 13 and up, book an adult haircut. Pricing may vary by hair length.",
    }, ...faq];
  }
  return {
    ...curated,
    slug,
    path: getServiceDetailPath(service.title, business),
    business,
    title: curated?.title ?? `${service.title} | ${brand}`,
    heading: service.title,
    description: isColor ? `${service.title} at ${brand}. Start with a quick service request.` : curated?.description ?? intro,
    intro,
    listDescription: isColor ? SERVICE_HUBS.color.listDescription : curated?.listDescription ?? description ?? content.intro.split(/(?<=\.)\s+/)[0],
    listPriceLabel: requiresRequest || fromPrice === undefined ? "To be determined" : undefined,
    fromPrice,
    priceLine,
    ctaLabel: requiresRequest ? "Request This Service" : curated?.ctaLabel ?? `Book ${service.title}`,
    ctaNote: requiresRequest ? SERVICE_HUBS.color.ctaNote : curated?.ctaNote,
    tipsTitle: isColor ? SERVICE_HUBS.color.tipsTitle : curated?.tipsTitle ?? "What to expect",
    expect: isColor ? SERVICE_HUBS.color.expect : curated?.expect ?? content.expect,
    faq,
    photoCategory: isColor ? "color" : curated?.photoCategory ?? content.photoCategory ?? (slug === "mens-haircut" ? "haircuts" : undefined),
    photosTitle: isColor ? "Latest color photos" : curated?.photosTitle ?? content.photosTitle ?? (slug === "mens-haircut" ? "Latest haircut photos" : undefined),
  };
};
